import { useEffect, useState } from "react";
import { trpc } from "../../../utils/trpc";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { DateTime } from "luxon";
import { useCurrentGuildData } from "../hooks/use_guild_data";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";

// CSS animation for shimmer effect
const shimmerAnimation = `
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
`;

type TimeFinderInstance = {
	id: string;
	title: string;
	description: string;
	createdAt: Date;
	lastModifiedAt: Date;
	responseCount: number;
	isPublic?: boolean;
};

export function TimeFinderList() {
	const guild = useCurrentGuildData();
	const guildId = guild?.id;
	const history = useHistory();
	const [page, setPage] = useState(0);
	const limit = 9;

	const timeFinderQuery = trpc.time_finder.getAllForGuild.useQuery(
		{ guild_id: guildId, page, limit },
		{
			enabled: !!guildId,
			onError: (error) => {
				console.error("Failed to fetch TimeFinder instances:", error);
			},
		}
	);

	const handleViewTimeFinder = (id: string) => {
		if (guildId) {
			history.push(
				common_utils.getTimeFinderURL({
					server_id: guildId,
					type: "view",
					instance_id: id,
				})
			);
		}
	};

	const handleCreateNew = () => {
		if (guildId) {
			history.push(
				common_utils.getTimeFinderURL({
					server_id: guildId,
					type: "create",
				})
			);
		}
	};

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	if (timeFinderQuery.isLoading) {
		return <LoadingSkeleton />;
	}

	const data = timeFinderQuery.data || {
		instances: [],
		pagination: { total: 0, page: 0, limit, totalPages: 0 },
	};
	const instances = data.instances;
	const pagination = data.pagination;

	if (instances.length === 0) {
		return (
			<div className="py-10 text-center">
				<h3 className="mb-2 text-xl font-medium">
					No Time Finder Instances
				</h3>
				<p className="mb-4 text-gray-500">
					You haven't created any time finders yet.
				</p>
				<SeshPrimaryButton onClick={handleCreateNew}>
					Create New TimeFinder
				</SeshPrimaryButton>
			</div>
		);
	}

	return (
		<div className="w-full space-y-4">
			<div className="mb-4 flex items-center justify-between">
				<h2 className="text-xl font-bold sm:text-2xl">
					Your Time Finder Instances
				</h2>
				<div className="w-fit">
					<SeshPrimaryButton
						onClick={handleCreateNew}
						className="whitespace-nowrap"
					>
						Create New
					</SeshPrimaryButton>
				</div>
			</div>

			<div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{instances.map((instance) => (
					<TimeFinderCard
						key={instance.id}
						instance={instance}
						onClick={() => handleViewTimeFinder(instance.id)}
					/>
				))}
			</div>

			{pagination.totalPages > 1 && (
				<div className="mt-4 flex justify-center">
					<div className="flex items-center space-x-4">
						<SeshPrimaryButton
							onClick={() =>
								handlePageChange(Math.max(0, page - 1))
							}
							disabled={page === 0}
							custom_size="small"
							className={page === 0 ? "opacity-50" : ""}
						>
							Previous
						</SeshPrimaryButton>
						<div className="min-w-[100px] text-center text-sm">
							Page {page + 1} of {pagination.totalPages}
						</div>
						<SeshPrimaryButton
							onClick={() =>
								handlePageChange(
									Math.min(
										pagination.totalPages - 1,
										page + 1
									)
								)
							}
							disabled={page >= pagination.totalPages - 1}
							custom_size="small"
							className={
								page >= pagination.totalPages - 1
									? "opacity-50"
									: ""
							}
						>
							Next
						</SeshPrimaryButton>
					</div>
				</div>
			)}
		</div>
	);
}

function TimeFinderCard({
	instance,
	onClick,
}: {
	instance: TimeFinderInstance;
	onClick: () => void;
}) {
	// Format the last modified date
	const formattedDate = DateTime.fromJSDate(
		new Date(instance.lastModifiedAt)
	).toRelative();

	return (
		<div
			className="flex h-[160px] w-full cursor-pointer flex-col rounded-lg border-2 border-gray-700 bg-base-dark p-3 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-sesh-primary hover:shadow-lg"
			onClick={onClick}
		>
			<div className="flex items-start gap-2">
				<i className="fas fa-calendar-check flex-shrink-0 text-xl text-sesh-primary"></i>
				<div className="min-w-0 flex-1">
					<div className="mb-1 flex items-center justify-between">
						<h3
							className="mr-2 truncate text-base font-semibold text-sesh-primary"
							title={instance.title}
						>
							{instance.title}
						</h3>
						{instance.isPublic !== false && (
							<div className="flex flex-shrink-0 items-center rounded bg-green-900/40 px-1.5 py-0.5 text-[10px] font-medium text-green-300">
								<i className="fas fa-globe mr-1"></i>
								<span>Public</span>
							</div>
						)}
					</div>
					<div className="h-[48px] overflow-hidden">
						<p className="line-clamp-2 text-xs text-gray-300">
							{instance.description || "No description"}
						</p>
					</div>
				</div>
			</div>

			<div className="mt-auto pt-2">
				<div className="h-px w-full bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>

				<div className="mt-2 flex justify-between text-xs text-gray-400">
					<div className="flex items-center">
						<i className="fas fa-clock text-sesh-primary-lighter mr-1"></i>
						<span className="">{formattedDate}</span>
					</div>
					<div className="flex items-center">
						<i className="fas fa-users text-sesh-primary-lighter mr-1"></i>
						<span>
							{instance.responseCount}{" "}
							{instance.responseCount === 1
								? "response"
								: "responses"}
						</span>
					</div>
				</div>
			</div>
		</div>
	);
}

function LoadingSkeleton() {
	return (
		<div className="w-full space-y-4">
			<style dangerouslySetInnerHTML={{ __html: shimmerAnimation }} />
			<div className="mb-4 flex items-center justify-between">
				<div className="relative h-8 w-64 overflow-hidden rounded-md bg-gray-700">
					<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
				</div>
				<div className="relative h-10 w-24 overflow-hidden rounded-md bg-gray-700">
					<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
				</div>
			</div>
			<div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
					<div
						key={i}
						className="w-full rounded-lg border border-gray-700 bg-base-dark p-4 shadow-sm"
					>
						<div className="flex items-start gap-3">
							<div className="relative h-6 w-6 flex-shrink-0 overflow-hidden rounded-md bg-gray-700">
								<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
							</div>
							<div className="min-w-0 flex-1">
								<div className="mb-2 flex items-center justify-between">
									<div className="relative h-6 w-3/5 overflow-hidden rounded-md bg-gray-700">
										<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
									</div>
									<div className="relative h-4 w-16 overflow-hidden rounded-md bg-gray-700">
										<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
									</div>
								</div>
								<div className="relative mb-2 h-4 w-full overflow-hidden rounded-md bg-gray-700">
									<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
								</div>
								<div className="relative mb-4 h-4 w-3/4 overflow-hidden rounded-md bg-gray-700">
									<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
								</div>
							</div>
						</div>
						<div className="relative mt-2 h-px w-full overflow-hidden bg-gray-700">
							<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
						</div>
						<div className="mt-3 flex justify-between">
							<div className="relative h-4 w-20 overflow-hidden rounded-md bg-gray-700">
								<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
							</div>
							<div className="relative h-4 w-20 overflow-hidden rounded-md bg-gray-700">
								<div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-600/30 to-transparent"></div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
